import { useEffect, useState } from "react";
import api from '../config/api';
import Container from "@mui/material/Container";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { Chip, Divider, Paper } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import useStore from "../config/store";
import TopBar from "../layouts/TopBar";
import UserGrid from "./UserGrid";
import UserOrganizationModal from "./UserOrganizationModal";
import UserPasswordModal from "./UserPasswordModal";
import UserModal from "./UserModal";
import { useTranslation } from 'react-i18next';

/**
 * Listado GLOBAL de usuarios de la plataforma (solo super-admin y
 * admin-grafton). Vive en /admin, que es además la pantalla a la que la app
 * redirige cuando NO hay organización seleccionada (App.js).
 *
 * FIX 2026-09-14 (Erwin): se elimina la verificación de límites de uso
 * (GET /organizations/:id/usage/check?action=users) que bloqueaba la pantalla.
 * Motivos:
 *  - Sin organización seleccionada —el estado NORMAL de /admin— la función
 *    hacía un return temprano sin apagar su flag de carga, y la pantalla
 *    quedaba pegada para siempre en "Verificando límites de uso...".
 *  - El backend (organization.model.checkUsageLimit) devuelve el límite de
 *    'users' como ilimitado (canPerform: true, limit: -1): nunca puede negar.
 *  - Sus dos alertas descartaban la respuesta con `if (action === 'users')
 *    return null`, así que no dibujaban nada en pantalla.
 *  - El alta de usuarios no depende de la organización seleccionada: UserModal
 *    crea contra la organización elegida DENTRO del modal.
 */
const UserLayout = () => {
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [orgData, setOrgData] = useState([]);
  const [counter, setCounter] = useState(0);

  const [openOrgModal, setOpenOrgModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openUserModal, setOpenUserModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState("add");

  const { authToken, showAlert, organization, user } = useStore.getState();
  const { t } = useTranslation();

  useEffect(() => {
    const loadData = async () => {
      try {
        const orgResponse = await api.get("/organizations", {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const orgs = orgResponse.data.response.data;
        setOrgData(orgs);

        const userResponse = await api.get("/users/allUsers", {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const allUsers = userResponse.data.response.data.map((u, index) => {
          const organizationNames = orgs
            .filter(org => u.organizations?.includes(org.uid))
            .map(org => org.name);

          const projectNames = orgs
            .flatMap(org => org.projects || [])
            .filter(project => u.projects?.includes(project.uid))
            .map(project => project.name);

          return {
            id: index + 1,
            uid: u.uid,
            name: u.name,
            email: u.email,
            rol: u.rol,
            // erpRole viaja hasta UserModal: sin él el selector "Rol ERP" se
            // dibujaba vacío aunque el usuario tuviera rol, y al guardar se
            // enviaba erpRole: null, borrándoselo (fix 2026-09-14).
            erpRole: u.erpRole,
            organizations: u.organizations,
            projects: u.projects,
            organizationNames,
            projectNames,
          };
        });

        setUsers(allUsers);
        setLoaded(true);
      } catch (err) {
        console.error(err);
        showAlert(t('users.loadError'), "warning");
        setError(true);
      }
    };

    loadData();
    // El listado es global (/users/allUsers): no depende de la organización
    // seleccionada, por eso no está en las dependencias.
  }, [counter, authToken, showAlert]);

  return (
    <Box>
      <TopBar />

      {!error ? (
        <Box sx={{ pt: "64px" }}>
          <Paper
            sx={{
              p: 1.5,
              pr: 3,
              backgroundColor: "#6A5AF9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "0px !important",
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "white", ml: 4, fontSize: "1rem" }}
            >
              {t('users.title')}
            </Typography>
          </Paper>

          <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Divider sx={{ width: "85%", m: "2rem auto", textAlign: "center" }}>
              <Chip
                sx={{
                  fontSize: "1.5rem",
                  backgroundColor: "transparent",
                  color: "#575757",
                }}
                label={t('users.subTitle')}
              />
            </Divider>

            {!loaded ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: "#6A5AF9", mb: 1 }}>
                  {t('users.userLoading')}
                </Typography>
                <LinearProgress color="inherit" sx={{ width: "80%" }} />
              </Box>
            ) : (
              <UserGrid
                users={users}
                onEditOrganizations={(user) => {
                  setSelectedUser(user);
                  setOpenOrgModal(true);
                }}
                onEditPassword={(user) => {
                  setSelectedUser(user);
                  setOpenPasswordModal(true);
                }}
                onEditUser={(user) => {
                  setSelectedUser(user);
                  setModalType("edit");
                  setOpenUserModal(true);
                }}
                onDeleteUser={(user) => {
                  setSelectedUser(user);
                  setModalType("delete");
                  setOpenUserModal(true);
                }}
                currentUserId={user.uid}
                currentUserRole={user.rol}
                setCounter={setCounter}
              />
            )}

            <Fab
              onClick={() => {
                setSelectedUser(null);
                setModalType("add");
                setOpenUserModal(true);
              }}
              color="warning"
              aria-label="add"
              sx={{
                position: "fixed",
                bottom: 32,
                right: 64,
              }}
            >
              <AddIcon />
            </Fab>
          </Container>

          <UserOrganizationModal
            open={openOrgModal}
            onClose={() => setOpenOrgModal(false)}
            user={selectedUser}
            allOrganizations={orgData}
          />

          <UserPasswordModal
            open={openPasswordModal}
            onClose={() => setOpenPasswordModal(false)}
            user={selectedUser}
          />

          <UserModal
            openModal={openUserModal}
            handleClose={() => setOpenUserModal(false)}
            type={modalType}
            selectedUser={selectedUser}
            authToken={authToken}
            organizationId={organization}
            showAlert={showAlert}
            setCounter={setCounter}
            allOrganizations={orgData}
            currentUserRole={user.rol}
          />
        </Box>
      ) : (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 3 }}>
          <ErrorIcon sx={{ color: "red", fontSize: "2.5rem" }} />
          <Typography variant="h6" component="h4" sx={{ color: "red" }}>
            {t('users.loadError')}
          </Typography>
        </Grid>
      )}
    </Box>
  );
};

export default UserLayout;
